import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/features/admin/api/adminApi';
import { CountryStats, DateStats, StatusStats } from '@/types/admin';

export interface DashboardData {
  usersTotal: number | null;
  placesTotal: number | null;
  favoritesCount: number | null;
  placesPerCountry: CountryStats[];
  usersCreatedByDate: DateStats[];
  placesPerStatus: StatusStats[];
  placesWithRating: number | null;
}

/**
 * Hook for fetching dashboard statistics using TanStack Query.
 * Fetches all dashboard data from adminApi.
 */
export const useDashboardStats = () => {
  const queryClient = useQueryClient();

  // Fetch users total
  const { data: usersTotalData, isLoading: isLoadingUsersTotal } = useQuery({
    queryKey: ['dashboard', 'usersTotal'],
    queryFn: () => adminApi.getUsersTotal(),
  });

  // Fetch places total
  const { data: placesTotalData, isLoading: isLoadingPlacesTotal } = useQuery({
    queryKey: ['dashboard', 'placesTotal'],
    queryFn: () => adminApi.getPlacesTotal(),
  });

  // Fetch favorites count
  const { data: favoritesCountData, isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['dashboard', 'favoritesCount'],
    queryFn: () => adminApi.getFavoritesCount(),
  });

  // Fetch places per country
  const { data: placesPerCountry = [], isLoading: isLoadingCountries } = useQuery({
    queryKey: ['dashboard', 'placesPerCountry'],
    queryFn: () => adminApi.getPlacesPerCountry(),
  });

  // Fetch users created by date
  const { data: usersCreatedByDate = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['dashboard', 'usersCreatedByDate'],
    queryFn: () => adminApi.getUsersCreatedByDate(),
  });

  // Fetch places per status
  const { data: placesPerStatus = [], isLoading: isLoadingStatus } = useQuery({
    queryKey: ['dashboard', 'placesPerStatus'],
    queryFn: () => adminApi.getPlacesPerStatus(),
  });

  // Fetch places with rating
  const { data: placesWithRating = null, isLoading: isLoadingRating } = useQuery({
    queryKey: ['dashboard', 'placesWithRating'],
    queryFn: () => adminApi.getPlacesWithRating(),
  });

  const isLoading = isLoadingUsersTotal || isLoadingPlacesTotal || isLoadingFavorites || 
                    isLoadingCountries || isLoadingUsers || isLoadingStatus || isLoadingRating;

  // Combine data into single structure
  const data: DashboardData = {
    usersTotal: usersTotalData?.totalUsers || null,
    placesTotal: placesTotalData?.totalPlaces || null,
    favoritesCount: favoritesCountData?.favoritesCount || null,
    placesPerCountry,
    usersCreatedByDate,
    placesPerStatus,
    placesWithRating,
  };

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  return {
    data,
    isLoading,
    error: null, // Individual queries handle their own errors
    refetch,
  };
};
